'use client';

import { ReactNode, useEffect, useState } from 'react';
import { twMerge } from 'tailwind-merge';

type TCheckbox = {
  id: string;
  label: string;
  description?: ReactNode;
  disabled?: boolean;
  defaultChecked?: boolean;
  onChange?: (val: boolean) => void;
  resetKey?: number;
  blacked?: boolean;
  required?: boolean;
  errorMessage?: string;
};

export const Checkbox = ({
  id,
  label,
  description,
  disabled = false,
  defaultChecked = false,
  onChange,
  resetKey,
  blacked = false,
  required = false,
  errorMessage,
}: TCheckbox) => {
  const [checked, setChecked] = useState(defaultChecked);
  const descId = description ? `${id}-desc` : undefined;

  useEffect(() => {
    setChecked(defaultChecked);
  }, [resetKey, defaultChecked]);

  const ringClasses = blacked
    ? 'peer-focus-visible:ring-[1.5px] peer-focus-visible:ring-background peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-action'
    : 'peer-focus-visible:ring-[1.5px] peer-focus-visible:ring-action/70 peer-focus-visible:ring-offset-1 peer-focus-visible:ring-offset-background';

  return (
    <div>
      <label
        htmlFor={id}
        className={twMerge(
          'group flex items-center gap-3 cursor-pointer select-none tracking-wider font-semibold text-sm pl-1',
          disabled && 'cursor-default opacity-80',
          blacked && 'text-background'
        )}
      >
        <input
          type="checkbox"
          id={id}
          name={id}
          className="sr-only peer"
          disabled={disabled}
          checked={checked}
          aria-describedby={descId}
          onChange={e => {
            setChecked(e.target.checked);
            onChange?.(e.target.checked);
          }}
        />

        <div
          className={twMerge(
            'w-5 h-5 rounded-lg border flex shrink-0 items-center justify-center transition-all duration-150 shadow-[inset_0_1px_1px_rgba(255,255,255,0.08)]',
            ringClasses,
            blacked
              ? 'border-background/80 bg-transparent hover:bg-transparent'
              : 'border-foreground/30 bg-transparent group-hover:bg-backgroundLight/60',
            checked &&
              (blacked
                ? 'border-background bg-transparent shadow-[0_0_6px_rgba(0,0,0,0.09)]'
                : 'border-action/80 bg-transparent shadow-[0_8px_18px_rgba(251,192,11,0.09)]'),
            disabled && 'border-[#515151]'
          )}
        >
          <div
            className={twMerge(
              'w-2.5 h-2.5 rounded-md transition-all duration-150 transform origin-center',
              checked
                ? blacked
                  ? 'scale-100 bg-background'
                  : 'scale-100 bg-action'
                : blacked
                  ? 'scale-0 opacity-0'
                  : 'scale-0 opacity-0 group-hover:opacity-70 group-hover:scale-75 group-hover:bg-foreground/30',
              disabled && 'opacity-30'
            )}
          />
        </div>

        <div className="flex flex-col">
          <span className="font-semibold">
            {label}
            {required && <span className="ml-0.5 text-red-600">*</span>}
          </span>
          {description && (
            <span
              id={descId}
              className={twMerge(
                'text-xs font-normal opacity-70',
                blacked ? 'text-background' : 'text-foreground',
                blacked && 'selection:bg-background selection:text-foreground'
              )}
            >
              {description}
            </span>
          )}
        </div>
      </label>

      {errorMessage && <span className="block text-xs text-red-600 mt-1">{errorMessage}</span>}
    </div>
  );
};
